import cn from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import { MdOutlineFileDownload, MdRefresh, MdSave, MdSwapHoriz } from 'react-icons/md';
import { AlertDialog } from '@Components/AlertDialog';
import { Button } from '@Components/Button';
import { Dropdown } from '@Components/Dropdown';
import { Appearance, Design, Placement, Size, StretchMode } from '@Components/designConstants';
import {
  ALLOCATION_CRITERIA_OPTIONS,
  PlacementAnalyticsRow,
  PostOptimizationResponse,
  TableRow,
  isTopLevelBudgetRow
} from './post-optimization-constants';
import {
  applyEditsToData,
  transformPostOptimizationData
} from './post-optimization-data-transform';
import { exportPostOptimizationToCSV } from './post-optimization-export';
import { PostOptimizationTable } from './PostOptimizationTable';
import { useAllocationCalculations } from './useAllocationCalculations';
import { PostOptimizationEditState, usePostOptimizationEdits } from './usePostOptimizationEdits';
import { useSaveReset } from './useSaveReset';

interface PostOptimizationViewProps {
  apiData: PostOptimizationResponse;
  company: string;
  runId: string;
  streamingType: string;
}

interface PendingReallocationDecision {
  rowId: string;
  edits: PostOptimizationEditState;
}

/**
 * Main view component for Post-Optimization allocation management
 *
 * Data Flow:
 * 1. Raw API data → transform → hierarchical structure
 * 2. Apply user edits → data with edits
 * 3. Calculate allocations → data with calculations
 * 4. Pass stable reference to react-table
 *
 * User edits trigger recalculation automatically via useMemo dependencies
 */
export const PostOptimizationView = ({
  apiData,
  company,
  runId,
  streamingType
}: PostOptimizationViewProps) => {
  const [allocationCriteria, setAllocationCriteria] = useState<keyof PlacementAnalyticsRow>(
    apiData.savedEdits?.allocation_criteria || ALLOCATION_CRITERIA_OPTIONS[0].value
  );
  const [pendingReallocation, setPendingReallocation] =
    useState<PendingReallocationDecision | null>(null);

  const { edits, updateEdit, resetChanges } = usePostOptimizationEdits();

  const transformedData = useMemo(() => transformPostOptimizationData(apiData), [apiData]);

  const {
    handleSave: handleSaveInternal,
    handleReset,
    isSaving,
    isResetting,
    originalEdits
  } = useSaveReset({
    allocationCriteria,
    runId,
    company,
    streamingType,
    apiData,
    resetChanges
  });

  // Line item IDs whose current edits differ from the saved baseline
  const unsavedChangeIds = useMemo(() => {
    const changed = new Set<string>();
    for (const [id, edit] of Array.from(edits.entries())) {
      const baseline = originalEdits.get(id);
      const baselineMustInclude = baseline?.mustInclude || false;
      const baselineMustExclude = baseline?.mustExclude || false;
      const baselineShouldReallocate = baseline?.shouldReallocateBudget || false;
      if (
        (edit.mustInclude || false) !== baselineMustInclude ||
        (edit.mustExclude || false) !== baselineMustExclude ||
        (edit.shouldReallocateBudget || false) !== baselineShouldReallocate
      ) {
        changed.add(id);
      }
    }
    return changed;
  }, [edits, originalEdits]);

  // Saved baseline edits overlaid with the user's in-session edits
  const allEditsToApply = useMemo(() => {
    const combined = new Map(originalEdits);
    edits.forEach((value, key) => {
      combined.set(key, value);
    });
    return combined;
  }, [originalEdits, edits]);

  const dataWithEdits = useMemo(
    () => applyEditsToData(transformedData, allEditsToApply, unsavedChangeIds),
    [transformedData, allEditsToApply, unsavedChangeIds]
  );

  const calculatedData = useAllocationCalculations(dataWithEdits, allocationCriteria);

  const handleUpdateEdit = useCallback(
    (row: TableRow, newEdits: PostOptimizationEditState) => {
      const normalizedEdits = newEdits.mustExclude
        ? newEdits
        : { ...newEdits, shouldReallocateBudget: false };

      if (newEdits.mustExclude && !row.mustExclude && isTopLevelBudgetRow(row)) {
        setPendingReallocation({ rowId: row.id, edits: normalizedEdits });
        return;
      }

      updateEdit(row.id, normalizedEdits);
    },
    [updateEdit]
  );

  const resolveBudgetReallocation = useCallback(
    (shouldReallocateBudget: boolean) => {
      if (!pendingReallocation) {
        return;
      }
      updateEdit(pendingReallocation.rowId, {
        ...pendingReallocation.edits,
        shouldReallocateBudget
      });
      setPendingReallocation(null);
    },
    [pendingReallocation, updateEdit]
  );

  const isBusy = isSaving || isResetting;

  const handleSave = () => {
    handleSaveInternal(calculatedData);
  };

  const handleExport = () => {
    exportPostOptimizationToCSV(calculatedData, company, runId);
  };

  return (
    <div className='post-optimization-view'>
      <div className='post-optimization-header'>
        <div
          aria-labelledby='allocation-criteria-label'
          className='allocation-criteria-selector'
          role='group'
        >
          <span className='allocation-criteria-label' id='allocation-criteria-label'>
            Allocation Criterion
          </span>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}

            stretch={StretchMode.FIT}
            value={allocationCriteria}
            options={ALLOCATION_CRITERIA_OPTIONS}
            onChange={setAllocationCriteria}
          />
        </div>

        <div className='header-actions'>
          <Button
            appearance={Appearance.EMPTY}
            className='post-optimization-export-btn'
            design={Design.NEUTRAL}
            tooltip={{ text: 'Export CSV', placement: Placement.BOTTOM }}
            aria-label='Export CSV'
            disabled={isBusy}
            onClick={handleExport}
            leadingElement={<MdOutlineFileDownload />}
          />
          <Button
            appearance={Appearance.EMPTY}
            className='reset-btn'
            design={Design.NEUTRAL}
            tooltip={{
              text: isResetting ? 'Resetting...' : 'Reset All',
              placement: Placement.BOTTOM
            }}
            aria-label='Reset all allocations'
            disabled={isBusy}
            onClick={handleReset}
            leadingElement={<MdRefresh className={cn({ spinning: isResetting })} />}
          />
          <Button
            appearance={Appearance.EMPTY}
            className='save-btn'
            design={Design.NEUTRAL}
            tooltip={{
              text: isSaving ? 'Saving...' : 'Save Allocations',
              placement: Placement.BOTTOM
            }}
            aria-label='Save allocations'
            disabled={isBusy}
            onClick={handleSave}
            leadingElement={
              <>
                <MdSave className={cn({ spinning: isSaving })} />
                {unsavedChangeIds.size > 0 && (
                  <span className='unsaved-changes-badge'>{unsavedChangeIds.size}</span>
                )}
              </>
            }
          />
        </div>
      </div>

      <PostOptimizationTable data={calculatedData} onUpdateEdit={handleUpdateEdit} />

      <AlertDialog
        escapable={false}
        show={pendingReallocation !== null}
        onHide={() => setPendingReallocation(null)}
        icon={<MdSwapHoriz size={48} />}
        title='Reallocate budget?'
        body='Would you like to proportionally reallocate this line item’s budget across the remaining line items?'
        primaryAction={{
          onClick: () => resolveBudgetReallocation(true),
          label: 'Yes, reallocate budget'
        }}
        secondaryAction={{
          onClick: () => resolveBudgetReallocation(false),
          label: 'No, don’t reallocate budget'
        }}
      />
    </div>
  );
};
